/**
 * How text on a floor plan is drawn: how big, and in what ink.
 *
 * One vocabulary for every kind of text on a plan — the space editor's
 * background labels, the annotations a planner writes on an event plan, and the
 * ones the Prism importer brings in. It is shared rather than per-workspace
 * because the importer (api) and the editor (web) both decide a size, and while
 * they each held their own number they disagreed: typed text came out at 20 in
 * one place and 16 in another with nothing saying which was right.
 *
 * Sizes are in design units, which on a plan are inches.
 */
export interface TextSize {
  value: number
  label: string
}

export const TEXT_SIZES: readonly TextSize[] = [
  { value: 12, label: 'Small' },
  { value: 16, label: 'Medium' },
  { value: 22, label: 'Large' },
  { value: 32, label: 'Extra large' },
]

/** What new text is written at, and what an unsized label is drawn at. */
export const DEFAULT_TEXT_SIZE = 16

/**
 * What the Prism importer gives an imported annotation.
 *
 * Deliberately not `DEFAULT_TEXT_SIZE`: it is the size imported plans have
 * always been drawn at, and re-pointing it would change how every future import
 * looks against the ones already in the database. It sits here so the two
 * numbers are read side by side, and so aligning them later is one edit.
 */
export const IMPORTED_TEXT_SIZE = 20

/**
 * The ink text is drawn in when it has no color of its own.
 *
 * One value for every kind of text on a plan. There used to be three — a label
 * created in the space editor was given `#37352f`, one that had never been
 * colored drew in the gray reserved for *lines*, and the export drew both in
 * `#2c2c2c` — so the same words changed shade depending on which of them last
 * had an opinion. Nothing sets a color at creation any more: text with no
 * color of its own is drawn in this one, everywhere, and a color is stored
 * only when somebody chooses it.
 */
export const DEFAULT_TEXT_COLOR = '#10241a' // Plan View's ink (`--ds-ink`), a forest-black

/**
 * The ink for text on a piece a source filter has pushed out of focus.
 *
 * Opaque rather than faded, like the muted fill and stroke beside it: pieces
 * that overlap would otherwise compound into something darker than either.
 */
export const MUTED_TEXT_COLOR = '#8a857c' // `--ds-ink-subtle`

/**
 * The size options for a piece of text, including its current size when that is
 * not one of the presets.
 *
 * Imported text is the reason: it arrives at `IMPORTED_TEXT_SIZE`, which is not
 * a preset. Without an option of its own the `<select>` would show blank, and
 * the first touch of the control would silently resize it.
 */
export function textSizeOptions(current: number): TextSize[] {
  if (TEXT_SIZES.some((size) => size.value === current)) return [...TEXT_SIZES]
  return [...TEXT_SIZES, { value: current, label: `Custom (${current})` }].sort(
    (a, b) => a.value - b.value,
  )
}
